import { API_BASE_URL } from "@/lib/config";

export const AUTH_EXPIRED_EVENT = "blosm:auth-expired";

function emitAuthExpired() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
}

function getErrorMessageFromPayload(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const p = payload as Record<string, unknown>;
  if (typeof p.message === "string") return p.message;
  if (typeof p.error === "string") return p.error;
  if (p.error && typeof p.error === "object") {
    const nested = p.error as Record<string, unknown>;
    if (typeof nested.message === "string") return nested.message;
  }
  return "";
}

function getPayloadStatusCode(payload: unknown): number | null {
  if (!payload || typeof payload !== "object") return null;
  const sc = (payload as Record<string, unknown>).statusCode;
  if (typeof sc === "number" && Number.isFinite(sc)) return sc;
  if (typeof sc === "string" && sc.trim() !== "") {
    const n = Number(sc);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Account no longer exists (deleted user) — clear local session. */
function isUserNotFoundResponse(res: Response, payload: unknown): boolean {
  const http404 = res.status === 404;
  const body404 = getPayloadStatusCode(payload) === 404;
  if (!http404 && !body404) return false;

  const msg = getErrorMessageFromPayload(payload).toLowerCase();
  if (msg.includes("user not found")) return true;

  if (payload && typeof payload === "object") {
    const err = (payload as Record<string, unknown>).error;
    if (err && typeof err === "object") {
      const nested = (err as Record<string, unknown>).message;
      if (typeof nested === "string" && nested.toLowerCase().includes("user not found")) return true;
    }
  }
  return false;
}

function maybeHandleAuthExpired(res: Response, payload: unknown): boolean {
  if (res.status === 401 || res.status === 403) {
    emitAuthExpired();
    return true;
  }
  if (isUserNotFoundResponse(res, payload)) {
    emitAuthExpired();
    return true;
  }
  const msg = getErrorMessageFromPayload(payload).toLowerCase();
  const mentionsAuth = /token|session|jwt|auth|login/.test(msg);
  const indicatesExpiry = /expired|invalid|unauthori|forbidden/.test(msg);
  if (mentionsAuth && indicatesExpiry) {
    emitAuthExpired();
    return true;
  }
  return false;
}

export type OtpPurpose = "login" | "signup";

export type SendOtpInput =
  | { purpose: "signup"; mobile: string; countryCode: string; email: string }
  | { purpose: "login"; mobile: string; countryCode: string }
  | { purpose: "login"; email: string };

function buildSendOtpBody(input: SendOtpInput): Record<string, string> {
  if (input.purpose === "signup") {
    return {
      mobile: input.mobile,
      countryCode: input.countryCode,
      email: input.email.trim(),
      purpose: "signup",
    };
  }
  if ("email" in input) {
    return { email: input.email.trim(), purpose: "login" };
  }
  return {
    mobile: input.mobile,
    countryCode: input.countryCode,
    purpose: "login",
  };
}

/**
 * Sends a login or signup OTP.
 * POST /api/v1/auth/send-otp
 */
export async function sendOtp(input: SendOtpInput): Promise<void> {
  const body = buildSendOtpBody(input);
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (
    !res.ok ||
    (json &&
      typeof json === "object" &&
      "success" in json &&
      (json as { success?: boolean }).success === false)
  ) {
    throw new Error(authErrorMessage(json, "Failed to send verification code"));
  }
}

/** Single wallet ledger row from `GET /users/me` and verify-otp user payload. */
export type WalletHistoryEntry = {
  _id: string;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  note: string | null;
  appointmentId: string | null;
  /** Set when the entry is linked to a booking. */
  appointmentName: string | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  createdAt: string;
};

function strOrNull(v: unknown): string | null {
  if (v == null || v === "") return null;
  return String(v);
}

function parseWalletHistoryEntry(raw: unknown): WalletHistoryEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Record<string, unknown>;
  const num = (v: unknown) =>
    typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v !== "" ? Number(v) || 0 : 0;
  return {
    _id: String(e._id ?? ""),
    type: String(e.type ?? "unknown"),
    amount: num(e.amount),
    balanceBefore: num(e.balanceBefore),
    balanceAfter: num(e.balanceAfter),
    note: e.note == null || e.note === "" ? null : String(e.note),
    appointmentId:
      e.appointmentId == null || e.appointmentId === "" ? null : String(e.appointmentId),
    appointmentName: strOrNull(e.appointmentName),
    appointmentDate: strOrNull(e.appointmentDate),
    appointmentTime: strOrNull(e.appointmentTime),
    createdAt: e.createdAt != null ? String(e.createdAt) : "",
  };
}

function parseWalletHistory(raw: unknown): WalletHistoryEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map(parseWalletHistoryEntry).filter((x): x is WalletHistoryEntry => x != null);
}

/** Matches backend `toPublicUser` (verify-otp + GET /users/me). */
export type PublicUser = {
  _id: string;
  username?: string;
  mobile: string;
  countryCode: string;
  name: string | null;
  email: string | null;
  wallet: number;
  walletHistory?: WalletHistoryEntry[];
  /** Signup + referral reward amounts from `user.bonuses` (when API returns it). */
  bonuses?: {
    signupBonus?: number;
    referralBonus?: number;
  };
};

export type AuthResult = {
  token: string;
  user: PublicUser;
  isFirstLogin: boolean;
};

/** @deprecated Use AuthResult */
export type VerifyOtpResult = AuthResult;

export type RegisterBody = {
  username: string;
  email: string;
  mobile: string;
  countryCode: string;
  password: string;
};

export type LoginBody = {
  email: string;
  password: string;
};

export type RedeemInviteCodeResult = {
  message?: string;
  creditedAmount?: number;
};

function parsePublicUser(raw: unknown): PublicUser {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid profile response");
  }
  const o = raw as Record<string, unknown>;
  const walletRaw = o.wallet;
  const wallet =
    typeof walletRaw === "number" && Number.isFinite(walletRaw)
      ? walletRaw
      : typeof walletRaw === "string" && walletRaw !== ""
        ? Number(walletRaw) || 0
        : 0;
  const wh = parseWalletHistory(o.walletHistory);
  const bonuses = parseBonuses(o.bonuses);
  return {
    _id: String(o._id ?? ""),
    username: o.username != null && o.username !== "" ? String(o.username) : undefined,
    mobile: String(o.mobile ?? ""),
    countryCode: String(o.countryCode ?? ""),
    name: o.name == null || o.name === "" ? null : String(o.name),
    email: o.email == null || o.email === "" ? null : String(o.email),
    wallet,
    ...(wh ? { walletHistory: wh } : {}),
    ...(bonuses ? { bonuses } : {}),
  };
}

function parseBonuses(raw: unknown): PublicUser["bonuses"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const b = raw as Record<string, unknown>;
  const toNum = (v: unknown): number | undefined => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };
  const signupBonus = toNum(b.signupBonus);
  const referralBonus = toNum(b.referralBonus);
  if (signupBonus === undefined && referralBonus === undefined) return undefined;
  const out: NonNullable<PublicUser["bonuses"]> = {};
  if (signupBonus !== undefined) out.signupBonus = signupBonus;
  if (referralBonus !== undefined) out.referralBonus = referralBonus;
  return out;
}

function parseBooleanField(raw: Record<string, unknown>, keys: string[]): boolean {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "boolean") return v;
  }
  return false;
}

function parseNumberField(raw: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function parseAuthEnvelope(json: unknown): AuthResult {
  if (!json || typeof json !== "object") {
    throw new Error("Invalid response");
  }
  const root = json as Record<string, unknown>;
  const data =
    root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : root;
  const token = data.token ?? root.token;
  const rawUser = data.user ?? root.user;
  if (!token || rawUser == null || typeof rawUser !== "object") {
    throw new Error("Invalid response");
  }
  const isFirstLogin =
    parseBooleanField(data, ["isFirstLogin", "isNewUser", "isNew", "firstLogin"]) ||
    parseBooleanField(root, ["isFirstLogin", "isNewUser", "isNew", "firstLogin"]) ||
    (rawUser && typeof rawUser === "object"
      ? parseBooleanField(rawUser as Record<string, unknown>, ["isFirstLogin", "isNewUser", "isNew", "firstLogin"])
      : false);
  return { token: String(token), user: parsePublicUser(rawUser), isFirstLogin };
}

function authErrorMessage(json: unknown, fallback: string): string {
  const msg = getErrorMessageFromPayload(json);
  return msg || fallback;
}

export async function registerUser(body: RegisterBody): Promise<AuthResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json && typeof json === "object" && "success" in json && (json as { success?: boolean }).success === false)) {
    throw new Error(authErrorMessage(json, "Failed to create account"));
  }
  return parseAuthEnvelope(json);
}

export async function loginWithEmail(email: string, password: string): Promise<AuthResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), password }),
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json && typeof json === "object" && "success" in json && (json as { success?: boolean }).success === false)) {
    throw new Error(authErrorMessage(json, "Invalid email or password"));
  }
  return parseAuthEnvelope(json);
}

export type VerifyOtpInput =
  | { purpose: "signup"; mobile: string; countryCode: string; email: string; otp: string }
  | { purpose: "login"; mobile: string; countryCode: string; otp: string }
  | { purpose: "login"; email: string; otp: string };

function buildVerifyOtpBody(input: VerifyOtpInput): Record<string, string> {
  const cleaned = input.otp.replace(/\s/g, "");
  if (input.purpose === "signup") {
    return {
      mobile: input.mobile,
      countryCode: input.countryCode,
      email: input.email.trim(),
      otp: cleaned,
      purpose: "signup",
    };
  }
  if ("email" in input) {
    return { email: input.email.trim(), otp: cleaned, purpose: "login" };
  }
  return {
    mobile: input.mobile,
    countryCode: input.countryCode,
    otp: cleaned,
    purpose: "login",
  };
}

/**
 * Completes OTP sign-in or signup — returns bearer token + public user envelope.
 * POST /api/v1/auth/verify-otp
 */
export async function verifyOtp(input: VerifyOtpInput): Promise<AuthResult> {
  const body = buildVerifyOtpBody(input);
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (
    !res.ok ||
    (json &&
      typeof json === "object" &&
      "success" in json &&
      (json as { success?: boolean }).success === false)
  ) {
    throw new Error(authErrorMessage(json, "Invalid or expired code"));
  }
  return parseAuthEnvelope(json);
}

export type UpdateMyProfileBody = {
  name?: string;
  email?: string;
  /** Digits only. Must be sent together with `countryCode`. */
  mobile?: string;
  countryCode?: string;
};

function parseUserFromApiPayload(json: unknown): PublicUser {
  if (!json || typeof json !== "object") {
    throw new Error("Invalid profile response");
  }
  const root = json as Record<string, unknown>;
  const payload = root.data !== undefined ? root.data : json;
  let userPayload: unknown = payload;
  if (
    payload &&
    typeof payload === "object" &&
    "user" in (payload as object) &&
    (payload as { user: unknown }).user != null
  ) {
    userPayload = (payload as { user: unknown }).user;
  } else if (root.user != null && payload === root) {
    userPayload = root.user;
  }
  return parsePublicUser(userPayload);
}

/**
 * PATCH /api/v1/users/me — partial profile update.
 * Send only fields to change: `name`, `email`, `mobile` + `countryCode` (pair).
 */
export async function updateMyProfile(
  token: string,
  body: UpdateMyProfileBody
): Promise<PublicUser> {
  const patch: Record<string, string> = {};
  if (body.name != null && body.name.trim() !== "") patch.name = body.name.trim();
  if (body.email != null && body.email.trim() !== "") patch.email = body.email.trim();
  if (body.mobile != null) patch.mobile = body.mobile.replace(/\D/g, "");
  if (body.countryCode != null && body.countryCode.trim() !== "") {
    patch.countryCode = body.countryCode.trim();
  }

  const hasMobile = "mobile" in patch;
  const hasCountry = "countryCode" in patch;
  if (hasMobile !== hasCountry) {
    throw new Error("Mobile and country code must be sent together.");
  }
  if (Object.keys(patch).length === 0) {
    throw new Error("Enter at least one field to update.");
  }

  const res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(patch),
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    maybeHandleAuthExpired(res, json);
    throw new Error(authErrorMessage(json, "Failed to update profile"));
  }
  if (json && typeof json === "object" && (json as { success?: boolean }).success === false) {
    maybeHandleAuthExpired(res, json);
    throw new Error(authErrorMessage(json, "Failed to update profile"));
  }
  try {
    return parseUserFromApiPayload(json);
  } catch {
    return getProfile(token);
  }
}

export async function redeemInviteCode(token: string, inviteCode: string): Promise<RedeemInviteCodeResult> {
  const cleanedCode = inviteCode.replace(/\D/g, "");
  if (!cleanedCode) {
    throw new Error("Enter a valid invite code");
  }

  const res = await fetch(`${API_BASE_URL}/api/v1/referrals/redeem`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ inviteCode: cleanedCode }),
    credentials: "include",
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json && typeof json === "object" && "success" in json && (json as { success?: boolean }).success === false)) {
    maybeHandleAuthExpired(res, json);
    const j = json as Record<string, unknown>;
    throw new Error(
      typeof j.message === "string"
        ? j.message
        : typeof j.error === "string"
          ? j.error
          : typeof j.error === "object" && j.error && typeof (j.error as { message?: unknown }).message === "string"
            ? String((j.error as { message?: unknown }).message)
            : "Failed to apply invite code"
    );
  }

  const data = json && typeof json === "object" && "data" in json && json.data && typeof json.data === "object"
    ? (json.data as Record<string, unknown>)
    : {};

  return {
    message:
      typeof data.message === "string"
        ? data.message
        : typeof (json as Record<string, unknown>).message === "string"
          ? String((json as Record<string, unknown>).message)
          : undefined,
    creditedAmount:
      parseNumberField(data, ["creditedAmount", "amount", "rewardAmount"]) ??
      parseNumberField(json as Record<string, unknown>, ["creditedAmount", "amount", "rewardAmount"]),
  };
}

export async function getProfile(token: string): Promise<PublicUser> {
  const res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    maybeHandleAuthExpired(res, json);
    throw new Error(authErrorMessage(json, "Failed to fetch profile"));
  }
  if (json && typeof json === "object" && (json as { success?: boolean }).success === false) {
    maybeHandleAuthExpired(res, json);
    throw new Error(authErrorMessage(json, "Failed to fetch profile"));
  }
  return parseUserFromApiPayload(json);
}

/** One row inside `serviceSelections` (multi-service booking). */
export type AppointmentServiceSelection = {
  serviceId: string;
  subheading: string;
  serviceItemName: string;
};

export type AppointmentGroupedService = {
  serviceId: string;
  serviceName: string;
  subServices: { name: string; price: number }[];
};

/**
 * POST /appointments body — either one line (`serviceId` + `subheading` + `serviceItemName`)
 * or multiple (`serviceSelections`). Do not send both shapes at once.
 */
export type BookAppointmentBody = {
  name: string;
  email?: string;
  mobile: string;
  countryCode?: string;
  date: string; // YYYY-MM-DD
  /** Optional; salon may confirm time later. */
  time?: string;
  notes?: string;
  /**
   * Parent salon **service** document id (API menu category / `services._id`).
   * Some backends require `service` or `serviceId` at the root; we send both when needed.
   */
  service?: string;
  /** Treatment / line id when distinct from parent `service`, else same as `service`. */
  serviceId?: string;
  subheading?: string;
  serviceItemName?: string;
  /** Multiple selections. */
  serviceSelections?: AppointmentServiceSelection[];
  /** Total estimated appointment duration in minutes. */
  estimatedTime?: number;
  /** Grouped service payload used by current appointments API. */
  services?: AppointmentGroupedService[];
};

export type SalonAvailability = {
  availableFrom: string;
  availableTo: string;
  createdAt?: string;
  updatedAt?: string;
};

export type BookedSlot = {
  date: string;
  start: string;
  end: string;
};

export async function getSalonAvailability(): Promise<SalonAvailability> {
  const res = await fetch(`${API_BASE_URL}/api/v1/availability`, {
    credentials: "include",
  });
  const json = await res.json();
  if (!res.ok || json.success === false) {
    throw new Error(json.message || json.error?.message || "Failed to fetch availability");
  }
  const data = json.data as SalonAvailability | undefined;
  if (!data?.availableFrom || !data?.availableTo) {
    throw new Error("Invalid availability response");
  }
  return data;
}

function parseBookedSlotRow(raw: unknown): BookedSlot | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const date = typeof r.date === "string" ? r.date.trim() : "";
  const start = typeof r.start === "string" ? r.start.trim() : "";
  const end = typeof r.end === "string" ? r.end.trim() : "";
  if (!date || !start || !end) return null;
  return { date, start, end };
}

export async function getAppointmentsBookedSlots(params?: {
  fromDate?: string;
  toDate?: string;
}): Promise<BookedSlot[]> {
  const query = new URLSearchParams();
  if (params?.fromDate) query.set("fromDate", params.fromDate);
  if (params?.toDate) query.set("toDate", params.toDate);
  const qs = query.toString();
  const res = await fetch(`${API_BASE_URL}/api/v1/appointments/booked-slots${qs ? `?${qs}` : ""}`, {
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof (json as { message?: unknown }).message === "string"
        ? String((json as { message: string }).message)
        : "Failed to fetch booked slots"
    );
  }

  const root = json as Record<string, unknown>;
  const source =
    (Array.isArray(root.data) && root.data) ||
    (root.data &&
      typeof root.data === "object" &&
      Array.isArray((root.data as Record<string, unknown>).bookedSlots)
      ? ((root.data as Record<string, unknown>).bookedSlots as unknown[])
      : null) ||
    (Array.isArray(root.bookedSlots) ? (root.bookedSlots as unknown[]) : null) ||
    (Array.isArray(json) ? (json as unknown[]) : []);

  return source.map(parseBookedSlotRow).filter((x): x is BookedSlot => x != null);
}

export type AvailableSlotsResponse = {
  slots: string[];
  duration: number;
  serviceTitle: string;
};

export async function getAvailableSlots(date: string, serviceId: string): Promise<AvailableSlotsResponse> {
  const params = new URLSearchParams({ date, serviceId });
  const res = await fetch(`${API_BASE_URL}/api/v1/appointments/available-slots?${params}`, {
    credentials: "include",
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.message || json.error?.message || "Failed to fetch available slots");
  }
  return json.data || { slots: [], duration: 30, serviceTitle: "" };
}

/** Public contact / enquiry — no auth. `mobile` must be digits only. */
export type EnquiryBody = {
  name: string;
  email: string;
  mobile: string;
  countryCode: string;
  message: string;
};

export async function submitEnquiry(body: EnquiryBody) {
  const res = await fetch(`${API_BASE_URL}/api/v1/enquiries`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof data.error === "string"
        ? data.error
        : typeof data.message === "string"
          ? data.message
          : "Failed to send message"
    );
  }
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(
      typeof data.message === "string" ? data.message : "Failed to send message"
    );
  }
  return data;
}

export async function bookAppointment(body: BookAppointmentBody, token: string) {
  if (!token?.trim()) {
    throw new Error("Please sign in to book an appointment.");
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  const res = await fetch(`${API_BASE_URL}/api/v1/appointments`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await res.json();
  const bookingErrMsg = (() => {
    if (typeof data?.message === "string" && data.message.trim()) return data.message.trim();
    const e = data?.error;
    if (typeof e === "string" && e.trim()) return e.trim();
    if (e && typeof e === "object" && typeof (e as { message?: unknown }).message === "string") {
      const m = (e as { message: string }).message.trim();
      if (m) return m;
    }
    return "Failed to book appointment";
  })();
  if (!res.ok) {
    maybeHandleAuthExpired(res, data);
    throw new Error(bookingErrMsg);
  }
  if (data && typeof data === "object" && data.success === false) {
    maybeHandleAuthExpired(res, data);
    throw new Error(bookingErrMsg);
  }
  return data;
}

/** One row from GET /appointments/my — matches typical backend payload. */
export type MyAppointment = {
  _id?: string;
  userId?: string;
  name?: string;
  email?: string;
  mobile?: string;
  countryCode?: string;
  service?: string;
  serviceId?: string;
  duration?: number;
  date?: string;
  time?: string;
  status?: string;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  price?: number | string;
};

/**
 * Normalizes API envelopes, e.g.
 * `{ success: true, data: [...] }` or `{ appointments: [...] }` or a bare array.
 */
export function parseMyBookingsList(json: unknown): MyAppointment[] {
  if (json == null) return [];
  if (Array.isArray(json)) return json as MyAppointment[];
  if (typeof json !== "object") return [];
  const o = json as Record<string, unknown>;
  const inner = o.data ?? o.appointments ?? o.results;
  if (Array.isArray(inner)) return inner as MyAppointment[];
  return [];
}

/** Raw JSON from GET /appointments/my (legacy). */
export async function getMyBookings(token: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/appointments/my`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) {
    maybeHandleAuthExpired(res, data);
    throw new Error(data.error || data.message || "Failed to fetch bookings");
  }
  if (data && typeof data === "object" && data.success === false) {
    maybeHandleAuthExpired(res, data);
    throw new Error(
      typeof data.message === "string" ? data.message : "Failed to fetch bookings"
    );
  }
  return data;
}

export async function getMyBookingsList(token: string): Promise<MyAppointment[]> {
  const json = await getMyBookings(token);
  return parseMyBookingsList(json);
}
